import { useState } from 'react';

/**
 * Hook for managing table row selection state
 *
 * This hook provides functionality for selecting/deselecting table rows,
 * supporting single and multiple selection modes with toggle behavior.
 *
 * @param initialState - Array of initially selected row IDs
 * @param onSelect - Optional callback when selection changes
 *
 * @returns Object containing:
 *   - selected: Array of currently selected row IDs
 *   - handleSelect: Function to toggle selection of a single row
 *   - handleSelectAll: Function to select/deselect all provided rows
 *   - isSelected: Function to check if a row is selected
 *
 * @example
 * ```tsx
 * const { selected, handleSelect, handleSelectAll, isSelected } = useSelection(
 *   ['row-1', 'row-3'], // Initially selected rows
 *   (selectedIds) => console.log('Selected rows:', selectedIds)
 * );
 *
 * // Toggle single row selection
 * handleSelect('row-2');
 *
 * // Select all rows
 * handleSelectAll(allRowIds, getRowId);
 *
 * // Check if row is selected
 * const rowSelected = isSelected('row-1');
 * ```
 */
export const useSelection = (
  initialState: (string | number)[] = [],
  onSelect?: (selected: (string | number)[]) => void,
) => {
  const [selected, setSelected] = useState<(string | number)[]>(initialState);

  const handleSelect = (id: string | number) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: (string | number)[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
    onSelect?.(newSelected);
  };

  const handleSelectAll = (
    rows: any[],
    getRowId: (row: any) => string | number,
  ) => {
    if (selected.length === rows.length) {
      setSelected([]);
      onSelect?.([]);
      return;
    }
    const newSelected = rows.map((row) => getRowId(row));
    setSelected(newSelected);
    onSelect?.(newSelected);
  };

  return { selected, handleSelect, handleSelectAll };
};
